import { Blocks } from "@/components/cms/Blocks";
import { PageSections, type SectionProps, type SectionRenderers } from "@/components/cms/PageSections";
import { SectionButtons, chosenFocalPoint, linkSlot, visibleLinks } from "@/components/cms/SectionRenderer";
import { ArrowRight, ButtonLink } from "@/components/ui/Button";
import { MediaFigure } from "@/components/ui/MediaFigure";
import { PageHeader } from "@/components/ui/PageHeader";
import { Reveal } from "@/components/ui/Reveal";
import { Section, SectionHeading } from "@/components/ui/Section";
import { useMediaByKey, useSettings } from "@/content-context";
import { cultureDialogue } from "@/data/media";
import { buildMetadata } from "@/lib/seo";
import { imageReveal } from "@/lib/motion";
import { pageLoader } from "@/page-data";

export const loader = pageLoader("media");

export const meta = () =>
  buildMetadata({
    title: "Media & dialogue",
    description:
      "The HISCUS Insights & Dialogue series: podcasts, documentaries and roundtables on waste, mental health, feminism and menstrual dignity.",
    path: "/media",
  });

function WatchSection({ section }: SectionProps) {
  const byKey = useMediaByKey();
  const image = section.mediaKey ? byKey(section.mediaKey) : cultureDialogue[0];
  const texts = section.texts ?? {};

  return (
    <Section>
      <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
        {image ? (
          <Reveal variants={imageReveal}>
            <MediaFigure
              item={image}
              ratio="16 / 9"
              sizes="(min-width: 1024px) 50vw, 100vw"
              objectPosition={chosenFocalPoint(section.settings?.focalPoint)}
            />
          </Reveal>
        ) : null}

        <Reveal>
          {section.title ? <h2 style={{ fontSize: "var(--step-h2)" }}>{section.title}</h2> : null}
          <Blocks blocks={section.body} className="mt-5" />
          <SectionButtons links={visibleLinks(section.ctas)} />

          {texts.noteTitle || texts.noteBody ? (
            <div className="mt-8 rounded-[14px] border border-line bg-mist p-5">
              {texts.noteTitle ? <h3 className="text-[0.9375rem] font-semibold text-ink">{texts.noteTitle}</h3> : null}
              {texts.noteBody ? <p className="mt-2 text-[0.9375rem] text-muted">{texts.noteBody}</p> : null}
            </div>
          ) : null}
        </Reveal>
      </div>
    </Section>
  );
}

function PressSection({ section }: SectionProps) {
  const settings = useSettings();
  const request = linkSlot(section, 0);

  return (
    <Section tone="mist" ariaLabelledBy="press-heading">
      <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <SectionHeading id="press-heading" eyebrow={section.eyebrow} title={section.title || "Using HISCUS material"} />
          <Blocks blocks={section.body} className="mt-6" />
          {request ? (
            <div className="mt-8">
              <ButtonLink href={request.href}>
                {request.label}
                <ArrowRight />
              </ButtonLink>
            </div>
          ) : null}
        </Reveal>

        {settings.socialLinks.length ? (
          <Reveal>
            <div className="rounded-[16px] border border-line bg-white p-6 md:p-8">
              <h2 style={{ fontSize: "var(--step-h4)" }}>{section.texts?.followTitle || "Follow HISCUS"}</h2>
              <ul className="mt-5 space-y-3">
                {settings.socialLinks.map((social) => (
                  <li key={social.href}>
                    <a
                      href={social.href}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex min-h-11 items-center font-semibold text-navy underline underline-offset-4"
                    >
                      {social.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ) : null}
      </div>
    </Section>
  );
}

/** "Subjects covered" is an ordinary card section and renders through the generic renderer. */
export const mediaSections: SectionRenderers = {
  "media.watch": WatchSection,
  "media.press": PressSection,
};

export function MediaPage() {
  return (
    <>
      <PageHeader
        eyebrow="Insights & Dialogue"
        title="Media, podcasts and public dialogue"
        lead="HISCUS records the conversations its programmes raise, so they outlive the room they happened in."
        trail={[
          { name: "Home", href: "/" },
          { name: "Media", href: "/media" },
        ]}
      />
      <PageSections renderers={mediaSections} />
    </>
  );
}
